// InteractiveButtonsBuilder.jsx
import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import PropTypes from "prop-types";
import SectionLabel from "../../../widgets/SectionLabel";
import PageTitle from "../../../widgets/PageTitle";
import SaveIcon from "@mui/icons-material/Save";
import { LoadingButton } from "@mui/lab";
import { API_ENDPOINT, MEDIA_TYPES } from "../../../utils/constants";
import MediaSelector from "../../../widgets/MediaSelector";
import WabaTextMsg from "../../../widgets/WabaTextMsg";
import { toast } from "react-toastify";
import { getHeader, toastError } from "../../../utils/util";
import axios from "axios";
import { nanoid } from "nanoid";
const HEADER_KINDS = ["none", "image", "video", "document", "text"];

const EMPTY_MSG = {
  type: "interactive_reply_buttons",
  header: { type: "none" },
  body: "",
  footer: "",
  buttons: [],
};

function InteractiveButtonsBuilder({
  value = EMPTY_MSG,
  isEdit = false,
  editId,
  preview = false,
  onUpdate,
}) {
  const [msg, setMsg] = useState(
    value?.header
      ? {
          ...value,
          ...(!value.buttons && { buttons: [] }),
          ...(!value.body && { body: "" }),
        }
      : {
          ...value,
          header: { type: "none" },
          ...(!value.buttons && { buttons: [] }),
          ...(!value.body && { body: "" }),
        }
  );
  const [btnText, setBtnText] = useState("");
  const [submitting, setSubmitting] = useState(false);

  /* -------- helpers -------- */
  const emit = (next) => {
    setMsg(next);
  };

  const changeHeaderType = (newType) => {
    const base = newType === "text" ? { text: "" } : { link: "" };
    emit({ ...msg, header: { type: newType, ...base } });
  };

  const updateHeaderContent = (val) => {
    const { type } = msg.header;
    const newHeader = { ...msg.header };

    if (type === "text") {
      newHeader.text = val;
    } else {
      newHeader.link = val;
    }
    emit({ ...msg, header: newHeader });
  };
  const updateHeaderFileName = (val) => {
    const newHeader = { ...msg.header };
    newHeader.filename = val;
    emit({ ...msg, header: newHeader });
  };

  const upsertButton = (idx, field, val) => {
    const m = { ...msg };
    m.buttons[idx][field] = val;
    emit({ ...m });
  };

  const addButton = () => {
    if (msg.buttons.length >= 3) return;
    emit({
      ...msg,
      buttons: [...msg.buttons, { id: nanoid(), title: "" }],
    });
  };

  const deleteButton = (idx) => {
    emit({
      ...msg,
      buttons: msg.buttons.filter((_, i) => i !== idx),
    });
  };
  const handleSubmit = async () => {
    const payload = { ...msg };
    if (payload.header.type === "text" && !payload.header.text) {
      toast.error("Please enter header text");
      return;
    } else if (
      MEDIA_TYPES.includes(payload.header.type.toUpperCase()) &&
      !payload.header.link
    ) {
      toast.error(`Please select ${payload.header.type}`);
      return;
    }

    if (!payload.body) {
      toast.error("Please enter body text");
      return;
    }
    if (!payload.buttons || payload.buttons.length < 1) {
      toast.error("Please select at least one button");
      return;
    }
    // Validate each button's reply.id and reply.title
    const hasEmptyFields = payload.buttons.some((btn, index) => {
      return !btn.id?.trim() || !btn.title?.trim();
    });

    if (hasEmptyFields) {
      toast.error("Each button must have a non-empty ID and Title");
      return;
    }
    if (!btnText && !isEdit && !preview) {
      toast.error(`Reply text is required!`);
      return;
    }
    const payloadObject = { ...payload, type: "interactive_reply_buttons" };
    if (isEdit) {
      payloadObject.id = editId;
      payloadObject.isEdit = true;
    } else {
      payloadObject.triggerText = btnText;
    }
    if (preview) {
      onUpdate?.({ ...payload, type: "interactive_reply_buttons" });
      return;
    }
    try {
      setSubmitting(true);
      const response = await axios.post(
        `${API_ENDPOINT}/message/save-message`,
        payloadObject,
        getHeader()
      );

      toast.success(response.data.message);
      setMsg(EMPTY_MSG);
      setBtnText("");
    } catch (error) {
      toastError(error);
    } finally {
      setSubmitting(false);
    }
  };

  /* -------- UI -------- */
  return (
    <Paper elevation={0} sx={{ px: 3, pt: 1 }}>
      <Stack spacing={4}>
        {/* Header */}
        <Box>
          <PageTitle>Interactive Buttons</PageTitle>
          <SectionLabel sx={{ my: 1 }}>Header</SectionLabel>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <Select
                size="small"
                fullWidth
                value={msg.header.type}
                onChange={(e) => changeHeaderType(e.target.value)}
              >
                {HEADER_KINDS.map((k) => (
                  <MenuItem key={k} value={k}>
                    {k.charAt(0).toUpperCase() + k.slice(1)}
                  </MenuItem>
                ))}
              </Select>
            </Grid>

            {MEDIA_TYPES.includes(msg.header.type.toUpperCase()) && (
              <>
                <Grid item sm={12}>
                  <MediaSelector
                    type={msg.header.type}
                    onChange={(newV) => {
                      updateHeaderContent(newV);
                    }}
                  />
                </Grid>
                {msg.header.type === "document" && (
                  <Grid item sm={12}>
                    <TextField
                      variant="outlined"
                      size="small"
                      label="Enter file name"
                      value={msg.header.filename}
                      onChange={(e) => {
                        updateHeaderFileName(e.target.value);
                      }}
                      fullWidth
                    />
                  </Grid>
                )}
              </>
            )}
            {msg.header.type === "text" && (
              <Grid item xs={12} sm={8}>
                <TextField
                  fullWidth
                  size="small"
                  label={
                    msg.header.type === "text"
                      ? "Header Text"
                      : `${
                          msg.header.type.charAt(0).toUpperCase() +
                          msg.header.type.slice(1)
                        } URL`
                  }
                  value={
                    msg.header.type === "text"
                      ? msg.header.text.text
                      : msg.header[msg.header.type].link
                  }
                  onChange={(e) => updateHeaderContent(e.target.value)}
                />
              </Grid>
            )}
          </Grid>
        </Box>
        <WabaTextMsg
          label="Body Text"
          value={msg.body}
          onChange={(newV) => {
            emit({ ...msg, body: newV });
          }}
        />

        {/* Footer */}
        <TextField
          size="small"
          label="Footer (optional)"
          fullWidth
          value={msg.footer || ""}
          onChange={(e) => emit({ ...msg, footer: e.target.value })}
        />
        <Box>
          <SectionLabel sx={{ my: 1 }}>Interactive Buttons (0–3)</SectionLabel>
          <Stack spacing={2}>
            {msg.buttons.map((btn, idx) => (
              <Grid container spacing={1} key={idx} alignItems="center">
                <Grid item xs={10}>
                  <TextField
                    size="small"
                    label="Button Title"
                    fullWidth
                    value={btn.title}
                    inputProps={{ maxLength: 20 }}
                    error={
                      btn.title.length > 20 || /[^a-zA-Z\s]/.test(btn.title)
                    }
                    helperText={
                      btn.title.length > 20 || /[^a-zA-Z\s]/.test(btn.title)
                        ? "Only letters and spaces allowed (max 20 chars)"
                        : "Only letters and spaces allowed (max 20 chars)"
                    }
                    onChange={(e) => {
                      const newValue = e.target.value
                        .replace(/[^a-zA-Z\s]/g, "")
                        .slice(0, 20);
                      upsertButton(idx, "title", newValue);
                    }}
                  />
                </Grid>
                <Grid item xs={2}>
                  <IconButton
                    color="error"
                    size="small"
                    onClick={() => deleteButton(idx)}
                    aria-label="delete"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Grid>
              </Grid>
            ))}

            <Stack justifyContent={"end"} direction={"row"}>
              <Button
                size="small"
                variant="outlined"
                onClick={addButton}
                disabled={msg.buttons.length >= 3}
              >
                Add Button
              </Button>
            </Stack>
            {!preview ? (
              <TextField
                fullWidth
                disabled={isEdit}
                sx={{ my: 2 }}
                label="Reply text"
                value={btnText}
                onChange={(e) => setBtnText(e.target.value)}
                variant="outlined"
                size="small"
              />
            ) : (
              <></>
            )}
            <Box justifyContent={"end"} display={"flex"}>
              <LoadingButton
                loading={submitting}
                onClick={handleSubmit}
                variant="contained"
                startIcon={<SaveIcon />}
              >
                Save
              </LoadingButton>
            </Box>
          </Stack>
        </Box>
      </Stack>
    </Paper>
  );
}

InteractiveButtonsBuilder.propTypes = {
  value: PropTypes.object,
  onChange: PropTypes.func,
};

export default InteractiveButtonsBuilder;
